/**
 * ArchiveBinPage — Archivage (Batch #14.69).
 *
 * Workflow 3 paliers :
 *  1. Actif → Archivé (via bouton Archive sur tuile Master)
 *  2. Archivé → Corbeille (via bouton "Mettre en corbeille" ici)
 *  3. Corbeille → Suppression définitive (dans TrashPage)
 *
 * Cette page liste les masters dans l'état "archivé MAIS PAS dans la corbeille".
 * Actions disponibles :
 *  - Restaurer (retour à l'état actif)
 *  - Mettre en corbeille (déplace vers TrashPage)
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  Archive, RotateCcw, Trash2, Search, Loader2, Building2, FolderOpen,
  FileSignature, ScrollText, ShieldCheck, Clock, Layers,
} from "lucide-react";
import UniversalArchiveSection from "@/components/admin/UniversalArchiveSection";

const ENTITY_LABELS = {
  lease: { label: "Bail", icon: FileSignature, color: "text-blue-600 bg-blue-50" },
  rl31: { label: "RL-31", icon: ScrollText, color: "text-amber-600 bg-amber-50" },
  rent_increase: { label: "Hausse loyer", icon: ScrollText, color: "text-rose-600 bg-rose-50" },
  credit_check: { label: "Enquête crédit", icon: ShieldCheck, color: "text-violet-600 bg-violet-50" },
};

export default function ArchiveBinPage() {
  const [tab, setTab] = useState("clients"); // clients | immeubles | universal
  const [items, setItems] = useState([]);
  const [immeubleItems, setImmeubleItems] = useState([]);
  const [universalCount, setUniversalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients-archive-bin");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadImmeubles = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/immeuble/archives/all");
      setImmeubleItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement (archives immeubles)");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "clients") loadClients();
    else if (tab === "immeubles") loadImmeubles();
  }, [tab, loadClients, loadImmeubles]);

  // Charge le compteur universel au montage pour le badge de l'onglet
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/archive/all").then((r) => {
      if (!cancelled) setUniversalCount(r.data?.total || 0);
    }).catch((e) => { console.error("[ArchiveBin] initial count failed:", e); });
    return () => { cancelled = true; };
  }, [tab]);

  const filtered = items.filter((m) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (m.first_name || "").toLowerCase().includes(s) ||
      (m.last_name || "").toLowerCase().includes(s) ||
      (m.couple_display_name || "").toLowerCase().includes(s) ||
      (m.email || "").toLowerCase().includes(s)
    );
  });

  const filteredImmeubles = immeubleItems.filter((a) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (a.label || "").toLowerCase().includes(s) ||
      (a.master_display_name || "").toLowerCase().includes(s) ||
      (a.entity_type || "").toLowerCase().includes(s)
    );
  });

  const restore = async (m) => {
    if (!window.confirm(`Réactiver "${m.couple_display_name || m.last_name || "ce dossier"}" ?\n\nIl reviendra dans la liste active.`)) return;
    setBusy(true);
    try {
      await api.post(`/masters/${m.id}/restore`);
      toast.success("Dossier réactivé ✓");
      loadClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const sendToTrash = async (m) => {
    if (!window.confirm(`Envoyer "${m.couple_display_name || m.last_name || "ce dossier"}" à la Corbeille ?\n\nIl sera marqué pour suppression définitive. Encore récupérable depuis la Corbeille.`)) return;
    setBusy(true);
    try {
      await api.post(`/masters/${m.id}/trash`);
      toast.success("Dossier envoyé à la corbeille");
      loadClients();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const restoreImmeuble = async (a) => {
    if (!window.confirm(`Restaurer "${a.label || 'cet élément'}" ?\n\nIl reviendra dans sa tuile d'origine.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/immeuble/archives/${a.id}/restore`);
      toast.success("Élément restauré ✓");
      loadImmeubles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const deleteImmeuble = async (a) => {
    if (!window.confirm(`Supprimer définitivement "${a.label || 'cet élément'}" ?\n\nCette action est irréversible.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/immeuble/archives/${a.id}`);
      toast.success("Supprimé définitivement");
      loadImmeubles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-2" data-testid="archive-bin-page">
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-2xl p-5 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
            <Archive className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-800">Archivage</h1>
            <p className="text-sm text-slate-600 mt-1">
              Dossiers cachés des listes actives. Réactivez-les ou envoyez-les à la corbeille pour une suppression définitive.
            </p>
            <p className="text-[11px] text-slate-500 mt-1 italic">
              Workflow : <b>Actif → Archivage → Corbeille → Suppression définitive</b>
            </p>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 mb-4 border-b border-slate-200">
        <button
          onClick={() => setTab("clients")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === "clients" ? "border-amber-500 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          data-testid="archive-bin-tab-clients"
        >
          <FolderOpen className="w-4 h-4 inline mr-1.5" />
          Dossiers clients ({items.length})
        </button>
        <button
          onClick={() => setTab("immeubles")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === "immeubles" ? "border-amber-500 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          data-testid="archive-bin-tab-immeubles"
        >
          <Building2 className="w-4 h-4 inline mr-1.5" />
          Archives Immeubles ({immeubleItems.length})
        </button>
        <button
          onClick={() => setTab("universal")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            tab === "universal" ? "border-amber-500 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          data-testid="archive-bin-tab-universal"
        >
          <Layers className="w-4 h-4 inline mr-1.5" />
          Autres items ({universalCount})
        </button>
      </div>

      {/* Search — masqué pour l'onglet universel (il a sa propre barre) */}
      {tab !== "universal" && (
      <div className="relative max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={tab === "clients" ? "Rechercher dans l'archivage..." : "Rechercher (label, master, type)..."}
          className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-full"
          data-testid="archive-bin-search"
        />
      </div>
      )}

      {/* ═══════════ Onglet UNIVERSEL (12 types métier) ═══════════ */}
      {tab === "universal" && (
        <UniversalArchiveSection
          mode="archive"
          onRefresh={() => api.get("/admin/archive/all").then((r) => setUniversalCount(r.data?.total || 0)).catch((e) => { console.error("[ArchiveBin] count refresh failed:", e); })}
        />
      )}

      {/* ═══════════ Onglet CLIENTS ═══════════ */}
      {tab === "clients" && (
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="inline w-7 h-7 animate-spin text-slate-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-400" data-testid="archive-bin-empty">
            {items.length === 0
              ? "Aucun dossier archivé."
              : "Aucun résultat pour cette recherche."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Dossier</th>
                <th className="text-left px-4 py-3">Archivé le</th>
                <th className="text-left px-4 py-3">Par</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr
                  key={m.id}
                  className="border-t border-slate-100 hover:bg-slate-50/50"
                  data-testid={`archive-bin-row-${m.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800 inline-flex items-center gap-1.5">
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-bold uppercase tracking-wider">
                        Couple
                      </span>
                      {m.couple_display_name || `${m.first_name || ""} ${m.last_name || ""}`.trim() || "(sans nom)"}
                    </div>
                    {m.email && (
                      <div className="text-[11px] text-slate-500 mt-0.5">{m.email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {m.archived_at
                      ? new Date(m.archived_at).toLocaleString("fr-CA", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {m.archived_by || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        onClick={() => restore(m)}
                        disabled={busy}
                        className="text-xs px-2.5 py-1 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`archive-bin-restore-${m.id}`}
                      >
                        <RotateCcw className="w-3 h-3" />
                        Réactiver
                      </button>
                      <button
                        onClick={() => sendToTrash(m)}
                        disabled={busy}
                        className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`archive-bin-to-trash-${m.id}`}
                        title="Envoyer à la corbeille (étape avant suppression définitive)"
                      >
                        <Trash2 className="w-3 h-3" />
                        Corbeille →
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}

      {/* ═══════════ Onglet IMMEUBLES ═══════════ */}
      {tab === "immeubles" && (
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="inline w-7 h-7 animate-spin text-slate-300" />
          </div>
        ) : filteredImmeubles.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-400" data-testid="archive-bin-immeubles-empty">
            {immeubleItems.length === 0
              ? "Aucune archive immeuble. Les baux, RL-31, hausses et enquêtes archivés via le bouton « Terminer » apparaîtront ici."
              : "Aucun résultat pour cette recherche."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Élément</th>
                <th className="text-left px-4 py-3">Dossier</th>
                <th className="text-left px-4 py-3">Archivé le</th>
                <th className="text-left px-4 py-3">Expire le</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredImmeubles.map((a) => {
                const def = ENTITY_LABELS[a.entity_type] || { label: a.entity_type, icon: Archive, color: "text-slate-600 bg-slate-50" };
                const Icon = def.icon;
                const expiresAt = a.expires_at ? new Date(a.expires_at) : null;
                const now = new Date();
                const daysLeft = expiresAt ? Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)) : null;
                return (
                  <tr
                    key={a.id}
                    className="border-t border-slate-100 hover:bg-slate-50/50"
                    data-testid={`archive-bin-immeuble-row-${a.id}`}
                  >
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider inline-flex items-center gap-1 ${def.color}`}>
                        <Icon className="w-3 h-3" />
                        {def.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-800 font-medium">
                      {a.label || "(sans label)"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {a.master_display_name || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {a.archived_at
                        ? new Date(a.archived_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {expiresAt ? (
                        <span className={`inline-flex items-center gap-1 ${daysLeft <= 3 ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                          <Clock className="w-3 h-3" />
                          {expiresAt.toLocaleDateString("fr-CA")}
                          <span className="text-[10px] opacity-70">({daysLeft}j)</span>
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={() => restoreImmeuble(a)}
                          disabled={busy}
                          className="text-xs px-2.5 py-1 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`archive-bin-immeuble-restore-${a.id}`}
                        >
                          <RotateCcw className="w-3 h-3" />
                          Restaurer
                        </button>
                        <button
                          onClick={() => deleteImmeuble(a)}
                          disabled={busy}
                          className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`archive-bin-immeuble-delete-${a.id}`}
                          title="Suppression définitive immédiate"
                        >
                          <Trash2 className="w-3 h-3" />
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      )}
    </div>
  );
}
